<template>
  <div class="iq-top-navbar">
    <div class="iq-navbar-custom">
      <nav
        class="navbar navbar-expand-lg navbar-light p-0 d-flex justify-content-between align-items-center"
        style="border-bottom: 1px solid #e2e8f0;"
      >
        <div class="w-100">
          <div class="d-flex h-100 px-3 py-2 align-items-center">
            <!-- Sidebar Toggle -->
            <div
              class="wrapper-menu d-flex align-items-center justify-content-center rounded-3 cursor-pointer"
              style="width: 38px; height: 38px; transition: all 0.2s;"
              @click.prevent="toggleSidebar"
            >
              <div class="main-circle">
                <i
                  class="fad fa-indent d-block d-md-none"
                  style="font-size: 18px; color: #475569;"
                />
                <i
                  class="fad fa-outdent d-none d-md-block"
                  style="font-size: 18px; color: #475569;"
                />
              </div>
              <div class="hover-circle">
                <i
                  class="fad fa-outdent d-block d-md-none"
                  style="font-size: 18px; color: #475569;"
                />
                <i
                  class="fad fa-indent d-none d-md-block"
                  style="font-size: 18px; color: #475569;"
                />
              </div>
            </div>

            <!-- Spacer -->
            <div class="flex-grow-1" />

            <!-- Right Actions -->
            <div class="d-flex align-items-center gap-2">
              <!-- Notifications -->
              <div
                id="popover-notification"
                class="d-flex align-items-center justify-content-center rounded-3 cursor-pointer position-relative"
                style="width: 38px; height: 38px; background: #f1f5f9; transition: all 0.2s;"
              >
                <i
                  class="fad fa-bell"
                  style="font-size: 18px; color: #475569;"
                />
                <span
                  v-if="notifications.unreadCount > 0"
                  class="position-absolute d-flex align-items-center justify-content-center"
                  style="top: -4px; right: -4px; width: 18px; height: 18px; border-radius: 999px; background: #ef4444; color: #fff; font-size: 10px; font-weight: 700;"
                >
                  {{ notifications.unreadCount > 99 ? '99+' : notifications.unreadCount }}
                </span>
              </div>
              <BPopover
                target="popover-notification"
                placement="bottom"
                custom-class="mt-2 shadow-sm notification-popover"
                boundary="viewport"
                boundary-padding="20px"
                @show="handleNotificationPopoverShow"
              >
                <template #title>
                  <div class="d-flex align-items-center justify-content-between">
                    <span
                      class="fw-bold"
                      style="font-size: 0.9rem;"
                    >Notifications</span>
                    <BLink
                      v-if="notifications.items.length && notifications.unreadCount > 0"
                      href="#"
                      class="text-decoration-none"
                      style="font-size: 0.75rem; color: #0f766e;"
                      @click.prevent="markAllNotificationsAsRead"
                    >
                      Mark all as read
                    </BLink>
                  </div>
                </template>
                <div style="width: 100%; max-height: 400px; overflow-y: auto; padding: 0; display: flex; flex-direction: column;">
                  <div
                    v-if="notifications.loading"
                    class="d-flex align-items-center justify-content-center flex-column"
                    style="color: #94a3b8; height: 200px; flex-shrink: 0;"
                  >
                    <i
                      class="fad fa-spinner-third fa-spin d-block mb-2"
                      style="font-size: 1.5rem;"
                    />
                    <small>Loading notifications...</small>
                  </div>
                  <template v-else-if="notifications.items.length">
                    <BLink
                      v-for="notification in notifications.items"
                      :key="notification.id"
                      href="#"
                      class="d-block text-decoration-none px-3 py-3"
                      style="border-bottom: 1px solid #eef2f7; word-wrap: break-word; overflow-wrap: break-word;"
                      :style="{ background: notification.read_at ? '#ffffff' : '#f8fafc' }"
                      @click.prevent="markNotificationAsRead(notification)"
                    >
                      <div class="d-flex align-items-start gap-2">
                        <div style="flex: 1; min-width: 0;">
                          <div
                            class="fw-semibold"
                            style="font-size: 0.8rem; color: #0f172a; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;"
                          >
                            {{ notification.message || 'Notification' }}
                          </div>
                          <small style="color: #94a3b8; font-size: 0.7rem; display: block; margin-top: 4px;">
                            {{ notificationTime(notification.created_at) }}
                          </small>
                        </div>
                        <span
                          v-if="!notification.read_at"
                          style="width: 8px; height: 8px; border-radius: 999px; background: #16a34a; margin-top: 4px; flex-shrink: 0;"
                        />
                      </div>
                    </BLink>
                  </template>
                  <div
                    v-else
                    class="d-flex align-items-center justify-content-center flex-column"
                    style="color: #cbd5e1; height: 200px; flex-shrink: 0;"
                  >
                    <i
                      class="fad fa-bell-slash d-block mb-2"
                      style="font-size: 2rem;"
                    />
                    <small>No notifications yet</small>
                  </div>
                </div>
              </BPopover>

              <!-- Dark Mode Toggle -->
              <div
                class="d-none d-md-flex align-items-center justify-content-center rounded-3 cursor-pointer"
                style="width: 38px; height: 38px; background: #f1f5f9; transition: all 0.2s;"
                @click.prevent="darkChange(!darkMode)"
              >
                <i
                  v-if="darkMode"
                  class="fad fa-brightness"
                  style="font-size: 18px; color: #f59e0b;"
                />
                <i
                  v-else
                  class="fad fa-moon"
                  style="font-size: 18px; color: #475569;"
                />
              </div>

              <!-- Profile -->
              <div class="close-open-icon">
                <span
                  id="popover-profile"
                  class="cursor-pointer d-flex align-items-center gap-2 rounded-3 p-1 px-2"
                  style="background: #f1f5f9; transition: all 0.2s;"
                >
                  <BAvatar
                    v-if="user.photo_url"
                    :src="getUserProfilePath"
                    variant="success"
                    size="32px"
                  />
                  <BAvatar
                    v-else
                    :text="getUserAbrevation"
                    variant="success"
                    size="32px"
                    style="font-weight: 700; font-size: 0.8rem;"
                  />
                  <div
                    class="d-none d-md-block"
                    style="line-height: 1.2;"
                  >
                    <div
                      class="fw-bold"
                      style="font-size: 0.85rem; color: #1e293b;"
                    >
                      {{ user ? user.name : null }}
                    </div>
                    <small style="font-size: 0.7rem; color: #64748b;">
                      {{ user ? user.role_name : null }}
                    </small>
                  </div>
                  <div
                    class="d-flex align-items-center"
                    style="width: 12px;"
                  >
                    <i
                      class="fad fa-chevron-right closed"
                      style="font-size: 10px; color: #94a3b8;"
                    />
                    <i
                      class="fad fa-chevron-down opened"
                      style="font-size: 10px; color: #94a3b8;"
                    />
                  </div>
                </span>
                <BPopover
                  target="popover-profile"
                  placement="bottom"
                  custom-class="mt-2 shadow-sm"
                  boundary-padding="20px"
                  triggers="click blur"
                  :auto-close="true"
                >
                  <template #title>
                    <div class="text-center py-1">
                      <h6 class="text-white mb-0 fw-bold">
                        {{ user ? user.name : null }}
                      </h6>
                      <small class="text-white-50">
                        {{ user ? user.role_name : null }}
                      </small>
                    </div>
                  </template>
                  <div style="width: 220px;">
                    <BOverlay
                      rounded
                      opacity="1"
                      spinner-variant="success"
                      :bg-color="darkMode ? '#2e2e30' : '#fff'"
                      :show="state.busy"
                    >
                      <BLink
                        href="#"
                        class="d-flex align-items-center gap-2 py-2 px-2 rounded-2 text-decoration-none"
                        style="transition: background 0.2s; color: #475569;"
                        @click.prevent="darkChange(!darkMode)"
                      >
                        <i
                          v-if="darkMode"
                          class="fad fa-brightness"
                          style="font-size: 1.1rem; color: #f59e0b;"
                        />
                        <i
                          v-else
                          class="fad fa-moon"
                          style="font-size: 1.1rem;"
                        />
                        <span>{{ darkMode ? 'Light' : 'Dark' }} Mode</span>
                      </BLink>
                      <RouterLink
                        :to="accountSettingsRoute"
                        class="d-flex align-items-center gap-2 py-2 px-2 rounded-2 text-decoration-none"
                        style="transition: background 0.2s; color: #475569;"
                      >
                        <i
                          class="fad fa-cog"
                          style="font-size: 1.1rem;"
                        />
                        <span>Account Settings</span>
                      </RouterLink>
                      <hr class="my-2">
                      <BLink
                        href="#"
                        class="d-flex align-items-center gap-2 py-2 px-2 rounded-2 text-decoration-none"
                        style="transition: background 0.2s; color: #ef4444;"
                        @click.prevent="clearUserSession"
                      >
                        <i
                          class="fad fa-sign-out"
                          style="font-size: 1.1rem;"
                        />
                        <span class="fw-bold">Log Out</span>
                      </BLink>
                    </BOverlay>
                  </div>
                </BPopover>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import { core, APPNAME } from '@/config/pluginInit'
import localforage from 'localforage'

import URL from '@/mixins/image-url'
import MxMisc from '@/mixins/misc'

import { SSharedAuth, SSharedNotification } from '@/services/shared'
import { SStudentAuth } from '@/services/student'

export default {
  name: 'Header',

  mixins: [
    MxMisc,
    URL
  ],

  data () {
    return {
      state: {
        busy: false
      },
      notifications: {
        loading: false,
        items: [],
        unreadCount: 0,
        pollHandle: null,
        visiblePollMs: 120000,
        noUnreadPollMs: 300000,
        hiddenPollMs: 600000,
        realtimeVisiblePollMs: 0,
        realtimeHiddenPollMs: 0,
        consecutiveErrors: 0,
        realtimeConnected: false,
        fetchPromise: null,
        syncHandle: null,
        lastFetchedAt: 0
      },
      keydownHandler: null,
      visibilityHandler: null,
      appName: APPNAME,
      notificationChannelName: null,
      notificationSubscription: null,
      echoStateChangeHandler: null
    }
  },

  computed: {
    ...mapGetters({
      darkMode: 'settings/darkModeState',
      loggingOut: 'customer-management/isUserLoggingOutState',
      user: 'auth/user',
      userType: 'auth/userType',
      token: 'auth/token'
    }),
    getUserAbrevation () {
      if (this.user?.name) {
        return String(this.user.name).charAt(0)
      }
      return ''
    },
    getUserProfilePath () {
      if (this.user?.photo_url) {
        return `${process.env.VUE_APP_API_BASE_URL}/${this.user.photo_url}`
      }

      return `${window.location.origin}/img/invalid-image.png`
    },
    accountSettingsRoute () {
      if (this.userType === 'student' || this.user?.role === 'student') {
        return { name: 'student.settings' }
      }

      return { name: 'shared.account.settings' }
    }
  },

  watch: {
    user: {
      handler () {
        this.subscribeNotificationRealtime()
      },
      deep: false
    },
    userType () {
      this.subscribeNotificationRealtime()
    },
    token () {
      this.subscribeNotificationRealtime()
    }
  },

  mounted () {
    this.fetchNotifications(true).finally(() => {
      this.scheduleNextNotificationPoll()
    })

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.fetchNotificationsIfStale(45000, true)
      }

      this.scheduleNextNotificationPoll()
    }

    document.addEventListener('visibilitychange', this.visibilityHandler)

    this.keydownHandler = event => {
      if (event.altKey && event.key === 'q') {
        this.swalConfirm({
          html: 'Logout Current Session?',
          confirmButtonText: 'Log Out',
          customClass: {
            confirmButton: 'btn btn-sm btn-danger'
          },
          preConfirm: () => this.clearUserSession()
        })
      }
    }

    window.addEventListener('keydown', this.keydownHandler)

    this.subscribeNotificationRealtime()
  },

  destroyed () {
    if (this.notifications.pollHandle) {
      clearTimeout(this.notifications.pollHandle)
      this.notifications.pollHandle = null
    }

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }

    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler)
      this.keydownHandler = null
    }

    if (this.notifications.syncHandle) {
      clearTimeout(this.notifications.syncHandle)
      this.notifications.syncHandle = null
    }

    this.unsubscribeNotificationRealtime()
  },

  methods: {
    setRealtimeConnected (connected) {
      const normalized = Boolean(connected)
      if (this.notifications.realtimeConnected === normalized) {
        return
      }

      this.notifications.realtimeConnected = normalized
      this.scheduleNextNotificationPoll()
    },

    bindEchoConnectionState () {
      const connection = window.Echo?.connector?.pusher?.connection
      if (!connection?.bind || this.echoStateChangeHandler) {
        return
      }

      this.echoStateChangeHandler = states => {
        if (states?.current !== 'connected') {
          this.setRealtimeConnected(false)
          return
        }

        this.setRealtimeConnected(true)
        this.fetchNotificationsIfStale(30000, true)
      }

      connection.bind('state_change', this.echoStateChangeHandler)
    },

    unbindEchoConnectionState () {
      const connection = window.Echo?.connector?.pusher?.connection
      if (connection?.unbind && this.echoStateChangeHandler) {
        connection.unbind('state_change', this.echoStateChangeHandler)
      }

      this.echoStateChangeHandler = null
    },

    getNotificationChannelName () {
      if (!this.user?.id) {
        return null
      }

      if (this.userType === 'student' || this.user?.role === 'student') {
        return `notifications.student.${this.user.id}`
      }

      if (this.userType === 'shared' || this.user?.role) {
        return `notifications.user.${this.user.id}`
      }

      return null
    },

    subscribeNotificationRealtime () {
      if (!window.Echo || !this.token) {
        this.unsubscribeNotificationRealtime()
        return
      }

      const channelName = this.getNotificationChannelName()
      if (!channelName) {
        this.unsubscribeNotificationRealtime()
        return
      }

      if (this.notificationChannelName === channelName && this.notificationSubscription) {
        return
      }

      this.unsubscribeNotificationRealtime()

      this.notificationChannelName = channelName
      this.bindEchoConnectionState()

      const subscription = window.Echo.private(channelName)
      this.notificationSubscription = subscription

      if (typeof subscription.subscribed === 'function') {
        subscription.subscribed(() => {
          this.setRealtimeConnected(true)
          this.fetchNotificationsIfStale(10000, true)
        })
      }

      if (typeof subscription.error === 'function') {
        subscription.error(() => {
          this.setRealtimeConnected(false)
        })
      }

      this.notificationSubscription
        .listen('.notification.created', event => {
          this.handleRealtimeNotificationCreated(event)
        })
    },

    unsubscribeNotificationRealtime () {
      if (this.notificationSubscription) {
        this.notificationSubscription.stopListening('.notification.created')
        this.notificationSubscription = null
      }

      if (window.Echo && this.notificationChannelName) {
        window.Echo.leave(this.notificationChannelName)
      }

      this.unbindEchoConnectionState()
      this.setRealtimeConnected(false)

      this.notificationChannelName = null
    },

    scheduleRealtimeSync () {
      if (this.notifications.syncHandle) {
        clearTimeout(this.notifications.syncHandle)
      }

      this.notifications.syncHandle = setTimeout(() => {
        this.notifications.syncHandle = null
        this.fetchNotificationsIfStale(120000, true)
      }, 15000)
    },

    handleRealtimeNotificationCreated (event) {
      const incoming = event?.notification
      if (!incoming) {
        this.fetchNotifications(true)
        return
      }

      const exists = this.notifications.items.some(item => item.id === incoming.id)
      if (!exists) {
        this.notifications.items = [incoming, ...this.notifications.items].slice(0, 12)
        if (!incoming.read_at) {
          this.notifications.unreadCount += 1
        }
      }

      this.scheduleRealtimeSync()
    },

    getNotificationPollDelay () {
      if (this.notifications.realtimeConnected) {
        const realtimeDelay = document.visibilityState !== 'visible'
          ? this.notifications.realtimeHiddenPollMs
          : this.notifications.realtimeVisiblePollMs

        return realtimeDelay > 0 ? realtimeDelay : null
      }

      if (document.visibilityState !== 'visible') {
        return this.notifications.hiddenPollMs
      }

      const baseDelay = this.notifications.unreadCount > 0
        ? this.notifications.visiblePollMs
        : this.notifications.noUnreadPollMs

      if (this.notifications.consecutiveErrors <= 0) {
        return baseDelay
      }

      const backoff = Math.min(4, this.notifications.consecutiveErrors)
      return baseDelay * backoff
    },

    scheduleNextNotificationPoll () {
      if (this.notifications.pollHandle) {
        clearTimeout(this.notifications.pollHandle)
        this.notifications.pollHandle = null
      }

      const delay = this.getNotificationPollDelay()
      if (!Number.isFinite(delay) || delay <= 0) {
        return
      }

      this.notifications.pollHandle = setTimeout(async () => {
        await this.fetchNotifications(true)
        this.scheduleNextNotificationPoll()
      }, delay)
    },

    toggleSidebar () {
      core.triggerSet()
    },

    async fetchNotificationsIfStale (maxAgeMs = 0, silent = true) {
      const now = Date.now()
      if (maxAgeMs > 0 && this.notifications.lastFetchedAt > 0) {
        if ((now - this.notifications.lastFetchedAt) < maxAgeMs) {
          return true
        }
      }

      return this.fetchNotifications(silent)
    },

    async fetchNotifications (silent = false) {
      if (this.notifications.fetchPromise) {
        return this.notifications.fetchPromise
      }

      if (!silent) {
        this.notifications.loading = true
      }

      const request = (async () => {
        try {
          const { data } = await SSharedNotification.getList({ per_page: 12 })
          const payload = data?.data || {}
          this.notifications.items = payload.items || []
          this.notifications.unreadCount = payload.unread_count || 0
          this.notifications.consecutiveErrors = 0
          this.notifications.lastFetchedAt = Date.now()
          return true
        } catch {
          this.notifications.consecutiveErrors += 1
          if (!silent) {
            this.notifications.items = []
            this.notifications.unreadCount = 0
          }
          return false
        } finally {
          if (!silent) {
            this.notifications.loading = false
          }
        }
      })()

      this.notifications.fetchPromise = request.finally(() => {
        this.notifications.fetchPromise = null
      })

      return this.notifications.fetchPromise
    },

    handleNotificationPopoverShow () {
      this.fetchNotificationsIfStale(10000, true)
    },

    async markNotificationAsRead (notification) {
      if (!notification || notification.read_at) {
        return
      }

      try {
        await SSharedNotification.markAsRead([notification.id])
        await this.fetchNotifications(true)
      } catch {
        // Silent fail to avoid blocking header interactions.
      }
    },

    async markAllNotificationsAsRead () {
      try {
        await SSharedNotification.markAsRead([])
        await this.fetchNotifications(true)
      } catch {
        // Silent fail to avoid blocking header interactions.
      }
    },

    notificationTime (value) {
      if (!value) {
        return 'Just now'
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return 'Just now'
      }

      return date.toLocaleString()
    },

    async clearUserSession () {
      if (!this.state.busy && !this.loggingOut) {
        this.unsubscribeNotificationRealtime()
        this.state.busy = true
        this.isLoggingOut(true)

        const logoutRequest = this.userType === 'student' || this.user?.role === 'student'
          ? SStudentAuth.logout()
          : SSharedAuth.logout()

        return logoutRequest.then(
          () => {
            this.isLoggingOut(false)

            this.setProvince('')
            this.setMunicipality('')
            this.setStatus('All')

            localforage.dropInstance({
              name: 'osas-table',
              storeName: this.user.role
            })

            this.$store.dispatch('auth/logout').finally(() => {
              this.$router.replace({ name: 'shared.login' })
            })

            return true
          }
        ).finally(() => {
          this.state.busy = false
        })
      }
    },

    darkChange (mode) {
      this.modeChange({ rtl: false, dark: mode })
    },

    ...mapActions({
      modeChange: 'settings/layoutModeAction',
      isLoggingOut: 'customer-management/isUserLoggingOutAction',
      setProvince: 'customer-management/customerProvinceAction',
      setMunicipality: 'customer-management/customerMunicipalityAction',
      setStatus: 'customer-management/customerStatusAction'
    })
  }
}
</script>

<style>
.notification-popover.popover {
  width: min(340px, calc(100vw - 24px));
  max-width: min(340px, calc(100vw - 24px));
}

.notification-popover .popover-body {
  padding: 0;
}

.notification-popover .popover-header > div {
  width: 100%;
}
</style>
